import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const GROUP_LABELS = {
  owner_created: { label: 'Genuine owner-created businesses', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  public_directory: { label: 'Publicly sourced directory listings', tone: 'text-blue-700 bg-blue-50 border-blue-200' },
  qa_test: { label: 'QA / automated test records (will be hidden)', tone: 'text-red-700 bg-red-50 border-red-200' },
  demo: { label: 'Demo / sample records (will be hidden)', tone: 'text-orange-700 bg-orange-50 border-orange-200' },
};

export default function AdminBusinessAudit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const headers = user ? { Authorization: `Bearer ${user.token}` } : {};
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const runAudit = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/business-audit`, { headers });
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!window.confirm('Apply classification tags and fix status mismatches now?\nQA/demo records will be hidden from public view. Genuine and directory listings stay visible. No data is deleted.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/business-audit/apply`, { applyTags: true, fixStatusMismatches: true }, { headers });
      toast.success(`Tagged ${res.data.tagged} businesses, fixed ${res.data.statusFixed.length} status mismatches`);
      runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-slate-800 text-white px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/admin/dashboard')} data-testid="audit-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="text-lg font-bold">Business Record Audit</h1>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
          <Button onClick={runAudit} disabled={busy} data-testid="audit-run-btn">
            <Search className="h-4 w-4 mr-2" /> Run audit (read-only)
          </Button>
          {report && (
            <Button onClick={apply} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="audit-apply-btn">
              <ShieldCheck className="h-4 w-4 mr-2" /> Apply tags + status fixes
            </Button>
          )}
          <p className="text-xs text-gray-500 w-full">
            The audit only reads data. Nothing changes until you press Apply. Applying hides QA/demo records from the public site (no deletion) and fixes approved businesses stuck on a pending status.
          </p>
        </div>

        {report && (
          <>
            <div className="bg-white rounded-xl border p-4" data-testid="audit-summary">
              <h2 className="font-semibold mb-2">Summary — {report.totalBusinesses} businesses</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {Object.entries(report.counts).map(([k, v]) => (
                  <div key={k} className={`rounded-lg border px-3 py-2 ${GROUP_LABELS[k].tone}`}>
                    <p className="text-xl font-bold">{v}</p>
                    <p className="text-xs">{GROUP_LABELS[k].label}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Pending tag changes: {report.pendingTagChanges}</p>
            </div>

            {report.statusMismatches.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="audit-mismatches">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4" /> Status mismatches (approved but status pending)
                </h3>
                {report.statusMismatches.map((b) => (
                  <p key={b.id} className="text-sm text-amber-900">• {b.businessName} — status: {b.status} → will become approved</p>
                ))}
              </div>
            )}

            {['qa_test', 'demo', 'owner_created'].map((k) => (
              <div key={k} className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold mb-2">{GROUP_LABELS[k].label} ({report.counts[k]})</h3>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {report.groups[k].map((b) => (
                    <div key={b.id} className="text-sm border-b border-gray-100 py-1.5" data-testid={`audit-row-${b.id}`}>
                      <span className="font-medium">{b.businessName}</span>
                      <span className="text-xs text-gray-500"> — {b.reason} · source={b.source} · approval={b.approvalStatus} · status={b.status || '—'} · claimed={String(b.isClaimed)} · created {String(b.createdAt || '').slice(0, 10)}</span>
                    </div>
                  ))}
                  {report.groups[k].length === 0 && <p className="text-sm text-gray-400">None</p>}
                </div>
              </div>
            ))}

            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-semibold mb-1">{GROUP_LABELS.public_directory.label} ({report.counts.public_directory})</h3>
              <p className="text-xs text-gray-500">These stay publicly visible. First 15 shown:</p>
              {report.groups.public_directory.slice(0, 15).map((b) => (
                <p key={b.id} className="text-sm text-gray-700">• {b.businessName} ({b.city})</p>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
